import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  X,
  Settings,
  ChevronLeft,
  ChevronRight,
  Check,
  Sparkles,
  Brain,
  Truck,
  Headphones,
  CalendarDays,
  Phone,
  MessageSquare,
  Mail,
  MessageCircle
} from 'lucide-react';
import { OperationalInfo, CARGO_COMPANIES } from '../../types/companyProfile';

interface OperationalInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialData?: OperationalInfo;
  onSave: (data: OperationalInfo) => void;
}

const CUSTOMER_SERVICE_TYPES = [
  { value: 'sales', label: 'Satış Öncesi', description: 'Ürün bilgisi, fiyat sorguları' },
  { value: 'support', label: 'Satış Sonrası', description: 'İade, şikayet, teknik destek' },
  { value: 'both', label: 'Her İkisi', description: 'Hem satış hem destek' }
];

const CUSTOMER_SERVICE_CHANNELS = [
  { value: 'phone', label: 'Telefon', icon: Phone },
  { value: 'chat', label: 'Canlı Chat', icon: MessageSquare },
  { value: 'email', label: 'E-posta', icon: Mail },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessageCircle }
];

const SEASONAL_PERIODS = [
  { value: 'yilbasi', label: 'Yılbaşı / Kış Sezonu', months: 'Aralık - Ocak' },
  { value: 'sevgililer', label: 'Sevgililer Günü', months: 'Şubat' },
  { value: 'anneler-babalar', label: 'Anneler/Babalar Günü', months: 'Mayıs - Haziran' },
  { value: 'yaz', label: 'Yaz Sezonu', months: 'Haziran - Ağustos' },
  { value: 'okul', label: 'Okul Dönemi', months: 'Eylül' },
  { value: 'black-friday', label: 'Black Friday', months: 'Kasım' },
  { value: '11-11', label: '11.11 / Bekarlar Günü', months: 'Kasım' },
  { value: 'ramazan', label: 'Ramazan Bayramı', months: 'Değişken' },
  { value: 'kurban', label: 'Kurban Bayramı', months: 'Değişken' }
];

const AI_LEARNING_MESSAGES = [
  { threshold: 1, message: 'Operasyonel analiz başlıyor...', subtext: 'Lojistik yapınızı anlıyoruz' },
  { threshold: 2, message: 'AI müşteri hizmetlerini analiz ediyor...', subtext: 'Servis kalitesi değerlendiriliyor' },
  { threshold: 3, message: 'Sezonsal kalıplar işleniyor...', subtext: 'Stok tahminleri hazırlanıyor' },
  { threshold: 4, message: 'Mükemmel! Operasyonel profil hazır...', subtext: 'Lojistik öneriler için hazırız' }
];

const STEPS = [
  { id: 'cargo', title: 'Kargo', icon: Truck },
  { id: 'service', title: 'Müşteri Hizmetleri', icon: Headphones },
  { id: 'seasonal', title: 'Sezonlar', icon: CalendarDays }
];

const OperationalInfoModal: React.FC<OperationalInfoModalProps> = ({
  isOpen,
  onClose,
  initialData,
  onSave
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<OperationalInfo>(initialData || {
    cargoCompanies: [],
    customerServiceHours: { enabled: false },
    seasonalPeriods: []
  });
  const [filledCount, setFilledCount] = useState(0);
  const [aiMessage, setAiMessage] = useState(AI_LEARNING_MESSAGES[0]);
  const [showAiAnimation, setShowAiAnimation] = useState(false);

  // Calculate filled fields
  useEffect(() => {
    let count = 0;
    if (formData.cargoCompanies && formData.cargoCompanies.length > 0) count++;
    if (formData.customerServiceHours?.enabled !== undefined) count++;
    if (formData.customerServiceHours?.type) count++;
    if (formData.seasonalPeriods && formData.seasonalPeriods.length > 0) count++;
    setFilledCount(count);

    // Update AI message
    const matchingMessage = [...AI_LEARNING_MESSAGES].reverse().find(m => count >= m.threshold);
    if (matchingMessage) {
      setAiMessage(matchingMessage);
    }

    // Show animation when field changes
    if (count > 0) {
      setShowAiAnimation(true);
      const timer = setTimeout(() => setShowAiAnimation(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [formData]);

  const toggleCargoCompany = (company: string) => {
    const current = formData.cargoCompanies || [];
    if (current.includes(company)) {
      setFormData(prev => ({
        ...prev,
        cargoCompanies: current.filter(c => c !== company)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        cargoCompanies: [...current, company]
      }));
    }
  };

  const toggleCustomerServiceEnabled = (enabled: boolean) => {
    setFormData(prev => ({
      ...prev,
      customerServiceHours: {
        ...prev.customerServiceHours,
        enabled
      }
    }));
  };

  const setCustomerServiceType = (type: 'sales' | 'support' | 'both') => {
    setFormData(prev => ({
      ...prev,
      customerServiceHours: {
        enabled: prev.customerServiceHours?.enabled ?? false,
        ...prev.customerServiceHours,
        type
      }
    }));
  };

  const toggleServiceChannel = (channel: 'phone' | 'chat' | 'email' | 'whatsapp') => {
    const current = formData.customerServiceHours?.channels || [];
    const updated = current.includes(channel)
      ? current.filter(c => c !== channel)
      : [...current, channel];
    setFormData(prev => ({
      ...prev,
      customerServiceHours: {
        enabled: prev.customerServiceHours?.enabled ?? false,
        ...prev.customerServiceHours,
        channels: updated
      }
    }));
  };

  const setWorkingHours = (hours: string) => {
    setFormData(prev => ({
      ...prev,
      customerServiceHours: {
        enabled: prev.customerServiceHours?.enabled ?? false,
        ...prev.customerServiceHours,
        workingHours: hours
      }
    }));
  };

  const toggleSeasonalPeriod = (period: string) => {
    const current = formData.seasonalPeriods || [];
    if (current.includes(period)) {
      setFormData(prev => ({
        ...prev,
        seasonalPeriods: current.filter(p => p !== period)
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        seasonalPeriods: [...current, period]
      }));
    }
  };

  const handleNext = () => {
    if (currentStep < STEPS.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      onSave(formData);
    }
  };

  const handleBack = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  if (!isOpen) return null;

  const modalContent = (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-2xl mx-4 bg-[#12121a] rounded-2xl border border-white/10 shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="relative px-6 py-4 border-b border-white/10 bg-gradient-to-r from-cyan-500/10 to-teal-500/10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-cyan-500/20 flex items-center justify-center">
                <Settings className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-white">Operasyonel Bilgiler</h2>
                <p className="text-sm text-gray-400">Adım {currentStep + 1} / {STEPS.length}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors"
            >
              <X className="w-5 h-5 text-gray-400" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-2 mt-4">
            {STEPS.map((step, index) => {
              const StepIcon = step.icon;
              return (
                <div
                  key={step.id}
                  className={`flex-1 flex items-center gap-2 px-3 py-2 rounded-lg transition-all ${
                    index === currentStep
                      ? 'bg-cyan-500/20 border border-cyan-500/50'
                      : index < currentStep
                        ? 'bg-cyan-500/10 border border-cyan-500/30'
                        : 'bg-white/5 border border-white/10'
                  }`}
                >
                  <StepIcon className={`w-4 h-4 ${
                    index === currentStep
                      ? 'text-cyan-400'
                      : index < currentStep
                        ? 'text-cyan-400'
                        : 'text-gray-500'
                  }`} />
                  <span className={`text-xs font-medium truncate ${
                    index === currentStep
                      ? 'text-cyan-300'
                      : index < currentStep
                        ? 'text-cyan-300'
                        : 'text-gray-500'
                  }`}>
                    {step.title}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Learning Banner */}
        <div className={`px-6 py-3 bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border-b border-white/5 transition-all duration-500 ${
          showAiAnimation ? 'opacity-100' : 'opacity-70'
        }`}>
          <div className="flex items-center gap-3">
            <div className={`relative ${showAiAnimation ? 'animate-pulse' : ''}`}>
              <Brain className="w-5 h-5 text-purple-400" />
              {showAiAnimation && (
                <Sparkles className="absolute -top-1 -right-1 w-3 h-3 text-yellow-400 animate-bounce" />
              )}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-300">{aiMessage.message}</p>
              <p className="text-xs text-gray-400">{aiMessage.subtext}</p>
            </div>
            <div className="text-right">
              <span className="text-lg font-bold text-white">{filledCount}</span>
              <span className="text-sm text-gray-400"> / 4</span>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[400px] overflow-y-auto">
          {/* Step 1: Cargo Companies */}
          {currentStep === 0 && (
            <div className="space-y-5">
              <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
                <p className="text-sm text-cyan-300">
                  💡 <strong>Not:</strong> İade oranı gibi metrikleri mağaza bağlantılarınızdan otomatik olarak çekiyoruz.
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Truck className="w-4 h-4 text-cyan-400" />
                  Anlaşmalı Kargo Firmaları
                </label>
                <p className="text-xs text-gray-500 mb-3">Birden fazla seçebilirsiniz</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {CARGO_COMPANIES.map((company) => (
                    <button
                      key={company}
                      type="button"
                      onClick={() => toggleCargoCompany(company)}
                      className={`px-3 py-2.5 rounded-xl text-sm font-medium transition-all text-left ${
                        formData.cargoCompanies?.includes(company)
                          ? 'bg-cyan-500/20 border-2 border-cyan-500 text-cyan-300'
                          : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                      }`}
                    >
                      {formData.cargoCompanies?.includes(company) && (
                        <Check className="w-3.5 h-3.5 inline mr-1" />
                      )}
                      {company}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Customer Service */}
          {currentStep === 1 && (
            <div className="space-y-5">
              {/* Has Customer Service */}
              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <Headphones className="w-4 h-4 text-cyan-400" />
                  Müşteri Hizmetleri Var mı?
                </label>
                <div className="flex gap-3">
                  <button
                    type="button"
                    onClick={() => toggleCustomerServiceEnabled(true)}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      formData.customerServiceHours?.enabled === true
                        ? 'bg-green-500/20 border-2 border-green-500 text-green-300'
                        : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    {formData.customerServiceHours?.enabled === true && <Check className="w-4 h-4 inline mr-2" />}
                    Evet
                  </button>
                  <button
                    type="button"
                    onClick={() => toggleCustomerServiceEnabled(false)}
                    className={`flex-1 px-4 py-3 rounded-xl font-medium transition-all ${
                      formData.customerServiceHours?.enabled === false
                        ? 'bg-red-500/20 border-2 border-red-500 text-red-300'
                        : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                    }`}
                  >
                    {formData.customerServiceHours?.enabled === false && <X className="w-4 h-4 inline mr-2" />}
                    Hayır
                  </button>
                </div>
              </div>

              {/* Service Details - Only if enabled */}
              {formData.customerServiceHours?.enabled && (
                <>
                  {/* Service Type */}
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">Hizmet Türü</label>
                    <div className="space-y-2">
                      {CUSTOMER_SERVICE_TYPES.map((type) => (
                        <button
                          key={type.value}
                          type="button"
                          onClick={() => setCustomerServiceType(type.value as 'sales' | 'support' | 'both')}
                          className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between ${
                            formData.customerServiceHours?.type === type.value
                              ? 'bg-cyan-500/20 border-2 border-cyan-500'
                              : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                          }`}
                        >
                          <div>
                            <p className={`font-medium ${
                              formData.customerServiceHours?.type === type.value ? 'text-cyan-300' : 'text-white'
                            }`}>
                              {type.label}
                            </p>
                            <p className="text-xs text-gray-500">{type.description}</p>
                          </div>
                          {formData.customerServiceHours?.type === type.value && (
                            <Check className="w-4 h-4 text-cyan-400" />
                          )}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Channels */}
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">İletişim Kanalları</label>
                    <div className="grid grid-cols-2 gap-2">
                      {CUSTOMER_SERVICE_CHANNELS.map((channel) => {
                        const Icon = channel.icon;
                        return (
                          <button
                            key={channel.value}
                            type="button"
                            onClick={() => toggleServiceChannel(channel.value as 'phone' | 'chat' | 'email' | 'whatsapp')}
                            className={`px-4 py-3 rounded-xl font-medium transition-all flex items-center gap-2 ${
                              formData.customerServiceHours?.channels?.includes(channel.value as 'phone' | 'chat' | 'email' | 'whatsapp')
                                ? 'bg-cyan-500/20 border-2 border-cyan-500 text-cyan-300'
                                : 'bg-white/5 border-2 border-white/10 text-gray-400 hover:border-white/30'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            {channel.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Working Hours */}
                  <div>
                    <label className="text-sm font-medium text-gray-300 mb-2 block">Çalışma Saatleri</label>
                    <input
                      type="text"
                      value={formData.customerServiceHours?.workingHours || ''}
                      onChange={(e) => setWorkingHours(e.target.value)}
                      placeholder="Örn: 09:00 - 18:00 (Hafta içi)"
                      className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-gray-500 focus:border-cyan-500/50 focus:ring-1 focus:ring-cyan-500/50 transition-all"
                    />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Step 3: Seasonal Periods */}
          {currentStep === 2 && (
            <div className="space-y-5">
              <div className="p-4 bg-cyan-500/10 rounded-xl border border-cyan-500/20">
                <p className="text-sm text-cyan-300">
                  💡 İşletmeniz için yoğun satış dönemlerini seçin. AI bu bilgiyi stok ve pazarlama önerilerinde kullanacak.
                </p>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-medium text-gray-300 mb-3">
                  <CalendarDays className="w-4 h-4 text-cyan-400" />
                  Yoğun Satış Dönemleri
                </label>
                <div className="space-y-2">
                  {SEASONAL_PERIODS.map((period) => (
                    <button
                      key={period.value}
                      type="button"
                      onClick={() => toggleSeasonalPeriod(period.value)}
                      className={`w-full p-3 rounded-xl text-left transition-all flex items-center justify-between ${
                        formData.seasonalPeriods?.includes(period.value)
                          ? 'bg-cyan-500/20 border-2 border-cyan-500'
                          : 'bg-white/5 border-2 border-white/10 hover:border-white/30'
                      }`}
                    >
                      <div>
                        <p className={`font-medium ${
                          formData.seasonalPeriods?.includes(period.value) ? 'text-cyan-300' : 'text-white'
                        }`}>
                          {period.label}
                        </p>
                        <p className="text-xs text-gray-500">{period.months}</p>
                      </div>
                      {formData.seasonalPeriods?.includes(period.value) && (
                        <Check className="w-4 h-4 text-cyan-400" />
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {formData.seasonalPeriods && formData.seasonalPeriods.length > 0 && (
                <div className="p-4 bg-white/5 rounded-xl border border-white/10">
                  <h4 className="text-sm font-medium text-white mb-2">📅 Seçilen Dönemler</h4>
                  <div className="flex flex-wrap gap-2">
                    {formData.seasonalPeriods.map((p) => {
                      const period = SEASONAL_PERIODS.find(sp => sp.value === p);
                      return period ? (
                        <span key={p} className="px-2 py-1 bg-cyan-500/20 text-cyan-300 rounded text-xs">
                          {period.label}
                        </span>
                      ) : null;
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-white/10 bg-[#0a0a12]/50 flex items-center justify-between">
          <button
            onClick={handleBack}
            disabled={currentStep === 0}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
              currentStep === 0
                ? 'text-gray-500 cursor-not-allowed'
                : 'text-gray-300 hover:bg-white/10'
            }`}
          >
            <ChevronLeft className="w-4 h-4" />
            Geri
          </button>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-gray-400 hover:text-white transition-colors"
            >
              Vazgeç
            </button>
            <button
              onClick={handleNext}
              className="flex items-center gap-2 px-5 py-2 bg-gradient-to-r from-cyan-500 to-teal-500 text-white rounded-lg font-medium hover:opacity-90 transition-opacity"
            >
              {currentStep === STEPS.length - 1 ? (
                <>
                  <Check className="w-4 h-4" />
                  Kaydet
                </>
              ) : (
                <>
                  İleri
                  <ChevronRight className="w-4 h-4" />
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.body);
};

export default OperationalInfoModal;
